'use es6';

import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIButton from 'UIComponents/button/UIButton';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import PortalIdParser from 'PortalIdParser';
import { useMutation } from 'data-fetching-client';
import EmailHelpText from '../shared/EmailHelpText';
import PropTypes from 'prop-types';
import { useTracker } from '../../utils/hooks/useTracker';
import { EVENTS } from '../../utils/usageTracker';
import { useControlsContext } from './context';
import { useHideDialog } from '../../dialog/ducks';
import { SEND_ASSET_NOW, CREATE_ASSET_SCHEDULE, EDIT_ASSET_SCHEDULE } from 'dashboard-lib/public/emails/emails';
import { getDialogText, sendNowSuccessMessage } from './shareAssetAlertMessages';
import { useRecurringShareAssets } from '../../data/hooks/useRecurringShareAssets';
import { SHARE_EMAIL, SHARE_SLACK } from './shareTypes';
import { showSuccessNotification } from '../shared/reportActionAlerts';
import { DEFAULT_EXPORT_TYPE } from '../shared/emailLogic';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REPORTS_LIST_BASE_URL = '/reports-list';
const SubmitButton = ({
  onSubmit,
  isRecurringShareAsset,
  disabled,
  currentDialog
}) => {
  const message = isRecurringShareAsset ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "reporting-action-components.actions.emailReport.dialog.footer.scheduleShareAssetButton",
    options: {
      dialog: getDialogText(currentDialog)
    }
  }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "reporting-action-components.actions.emailReport.dialog.footer.sendNowButton"
  });
  return /*#__PURE__*/_jsx(UIButton, {
    onClick: onSubmit,
    use: "primary",
    disabled: disabled,
    "data-test-id": "email-report-confirm",
    children: message
  }, "save");
};
const PreviewAssetButton = ({
  usageTracker,
  assetSpecificTrackingProps,
  objectType,
  previewUrl,
  lowerCasedDialog
}) => {
  return /*#__PURE__*/_jsx(UIButton, {
    onClick: () => {
      if (usageTracker) {
        usageTracker.track(EVENTS.clickPreviewShareAsset, Object.assign({
          object: lowerCasedDialog,
          actionType: 'view',
          action: 'open',
          interactionSource: 'dialog',
          assetType: objectType.toLowerCase()
        }, assetSpecificTrackingProps));
      }
    },
    href: previewUrl,
    target: "_blank",
    use: "secondary",
    external: true,
    "data-test-id": "email-report-preview",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.footer.previewButton"
    })
  }, "preview");
};
const CancelButton = ({
  hideDialog
}) => {
  return /*#__PURE__*/_jsx(UIButton, {
    onClick: hideDialog,
    use: "secondary",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.footer.cancelButton"
    })
  }, "cancel");
};
const ShareAssetDialogFooter = () => {
  var _thirdPartyApp$name;
  const {
    currentDialog,
    dataExportType,
    formattedExternalRecipientSet,
    disableSubmit,
    appUsageTracker,
    isRecurringShareAsset,
    objectType,
    reportId,
    assetSpecificTrackingProps,
    payload,
    thirdPartyApp
  } = useControlsContext();
  const usageTracker = useTracker(appUsageTracker);
  const [sendAssetNowMutation] = useMutation(SEND_ASSET_NOW);
  const [editAssetScheduleMutation] = useMutation(EDIT_ASSET_SCHEDULE);
  const [createAssetScheduleMutation] = useMutation(CREATE_ASSET_SCHEDULE);
  const {
    refetch
  } = useRecurringShareAssets({
    objectId: reportId,
    objectType,
    deliveryType: thirdPartyApp ? SHARE_SLACK : SHARE_EMAIL
  });
  const hasAttachedDataExport = dataExportType !== DEFAULT_EXPORT_TYPE.NONE;
  const hideDialog = useHideDialog();
  const lowerCasedDialog = thirdPartyApp === null || thirdPartyApp === void 0 || (_thirdPartyApp$name = thirdPartyApp.name) === null || _thirdPartyApp$name === void 0 ? void 0 : _thirdPartyApp$name.toLowerCase();
  const previewUrl = `${REPORTS_LIST_BASE_URL}/${PortalIdParser.get()}/export/${reportId}`;
  const shouldRenderExternalEmailWarningMsg = !isRecurringShareAsset && formattedExternalRecipientSet.size > 0 && !disableSubmit;
  const getDomainsFromExternalRecipientEmails = externalRecipientEmails => {
    return [...new Set(externalRecipientEmails.map(email => email.split('@')[1]))];
  };
  const sendAssetNow = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.sendReportShareAssetNow, Object.assign({
        object: lowerCasedDialog,
        actionType: 'share',
        action: `send-${lowerCasedDialog}`,
        interactionSource: 'dialog',
        interactionWith: 'asset-action',
        assetType: objectType.toLowerCase(),
        externalEmailDomains: getDomainsFromExternalRecipientEmails(payload.externalRecipientEmails),
        exportType: payload.exportType
      }, assetSpecificTrackingProps));
    }
    sendAssetNowMutation({
      variables: Object.assign({
        objectId: reportId,
        objectType
      }, payload)
    }).then(() => {
      sendNowSuccessMessage(currentDialog);
      hideDialog();
    }).catch(err => {
      const errObj = Object.getOwnPropertyDescriptor(err, 'networkError').value;
      if (errObj.status === 403) {
        const {
          responseJSON: {
            externalEmailsLimitDescription
          }
        } = errObj;
        ReportActionsFloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.emailNowWExceedsExternalEmailsLimit.title",
            options: {
              totalLimit: externalEmailsLimitDescription.totalLimit
            }
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.emailNowWExceedsExternalEmailsLimit.description",
            options: {
              currentUsage: externalEmailsLimitDescription.currentUsage
            }
          }),
          type: 'danger'
        });
      } else {
        ReportActionsFloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.shareAssetNowError.title",
            options: {
              dialog: getDialogText(currentDialog)
            }
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.emailReport.dialog.alert.shareAssetNowError.description"
          }),
          type: 'danger'
        });
      }
    });
  };
  const createOrEditAssetSchedule = () => {
    const {
      scheduledEmailId: scheduledShareAssetId
    } = payload;
    if (usageTracker) {
      if (scheduledShareAssetId) {
        usageTracker.track(EVENTS.updateSavedRecurringShareAsset, Object.assign({
          object: lowerCasedDialog,
          actionType: 'save',
          action: 'edit-schedule',
          interactionSource: 'dialog',
          interactionWith: 'asset-action',
          assetType: objectType.toLowerCase(),
          exportType: payload.exportType
        }, assetSpecificTrackingProps));
      } else {
        usageTracker.track(EVENTS.createRecurringShareAssets, Object.assign({
          object: lowerCasedDialog,
          actionType: 'share',
          action: `create-recurring-${lowerCasedDialog}`,
          interactionSource: 'dialog',
          interactionWith: 'asset-action',
          assetType: objectType.toLowerCase(),
          exportType: payload.exportType
        }, assetSpecificTrackingProps));
      }
    }
    const mutation = scheduledShareAssetId ? editAssetScheduleMutation : createAssetScheduleMutation;
    mutation({
      variables: Object.assign({
        objectId: reportId,
        objectType
      }, payload)
    }).then(() => {
      showSuccessNotification({
        message: 'reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetSuccess',
        options: {
          dialog: getDialogText(currentDialog)
        }
      });
      if (scheduledShareAssetId) {
        refetch();
      }
      hideDialog();
    }).catch(() => {
      ReportActionsFloatingAlertStore.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetError.title",
          options: {
            dialog: getDialogText(currentDialog)
          }
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.alert.scheduleShareAssetError.description"
        }),
        type: 'danger'
      });
    });
  };
  const sendOrScheduleAsset = () => {
    if (!isRecurringShareAsset) {
      sendAssetNow();
    } else {
      createOrEditAssetSchedule();
    }
  };
  const handleSubmit = e => {
    e.preventDefault();
    sendOrScheduleAsset();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [currentDialog === SHARE_EMAIL && /*#__PURE__*/_jsx(EmailHelpText, {
      shouldRenderExternalEmailWarningMsg: shouldRenderExternalEmailWarningMsg
    }), /*#__PURE__*/_jsx(SubmitButton, {
      isRecurringShareAsset: isRecurringShareAsset,
      onSubmit: handleSubmit,
      disabled: disableSubmit(),
      currentDialog: currentDialog
    }), !hasAttachedDataExport && /*#__PURE__*/_jsx(PreviewAssetButton, {
      usageTracker: usageTracker,
      assetSpecificTrackingProps: assetSpecificTrackingProps,
      objectType: objectType,
      previewUrl: previewUrl,
      lowerCasedDialog: lowerCasedDialog
    }), /*#__PURE__*/_jsx(CancelButton, {
      hideDialog: hideDialog
    })]
  });
};
ShareAssetDialogFooter.propTypes = {
  onSubmit: PropTypes.func
};
export default ShareAssetDialogFooter;